import * as Y from 'yjs'

import { debug } from 'src/services/DebugService'
import { mergeUpdatesChunked } from 'src/utils/yjs'

import {
    COMPACT_FORCE_THRESHOLD_BYTES,
    COMPACT_OPPORTUNISTIC_THRESHOLD_BYTES,
    CURRENT_SCHEMA_VERSION,
    HYDRATE_IDB_TIMEOUT_MS,
    MAX_CACHED_SESSIONS,
    WRITE_THROTTLE_MS,
} from './constants'
import {
    appendChunk,
    compactSession,
    deleteSession,
    findSessionsToEvict,
    getChunks,
    getSession,
    openCacheDb,
    putSession,
} from './idbStorage'
import type { ChunkRecord, HydrateResult, SessionRecord, TranscriptDocCacheOptions } from './types'

type SessionState = {
    schemaVersion: number
    nextSequence: number
    totalBytes: number
}

/**
 * IndexedDB-backed Y.Doc cache built on an append-only delta log. One instance
 * is bound to a single Y.Doc for its lifetime; `RtBrokerTranscriptClientV2.init()`
 * calls `init()` to hydrate from IDB and attach write listeners before
 * `transport.connect()`.
 *
 * Failure modes (open error, quota exceeded, corrupt chunk, schema mismatch)
 * all degrade silently to "no cache for this session"; the doc syncs from the
 * server as if the cache wasn't there.
 *
 * Why an append-only log instead of one full `encodeStateAsUpdate` per flush:
 * encoding the whole doc scales with total session length (250 ms+ freeze on
 * 8 h transcripts). Appending the per-flush delta keeps the hot-path work
 * proportional to recent activity. Compaction relocates the encode-equivalent
 * cost to moments the user can't perceive (destroy, visibilitychange-hidden);
 * `COMPACT_FORCE_THRESHOLD_BYTES` bounds the rare unavoidable mid-session
 * compaction.
 *
 * Cancellation is best-effort: `init()`'s `HYDRATE_IDB_TIMEOUT_MS` and
 * `destroy()` flip a flag that the awaited `_hydrateInternal` checks after
 * every IDB round-trip. The deadline's scope is deliberately the IDB-await
 * phase (open / getSession / getChunks / cleanup deleteSession) -- that's
 * where pathological slowdowns happen (locked DB across tabs, slow disks,
 * quota exhaustion, blocked-on-upgrade). The synchronous
 * `mergeUpdatesChunked` + `Y.applyUpdate` pair runs to completion regardless;
 * its cost is bounded by `COMPACT_FORCE_THRESHOLD_BYTES`, which forces
 * compaction before the log can grow large enough for merge to dominate. A
 * true wall-clock cap on the merge phase would require a Web Worker; we
 * accept the gap.
 *
 * Two consequences worth noting, neither of which produces an inconsistent
 * outcome but both of which can waste work:
 *   - An IDB cursor walk that was already in flight when the deadline fires
 *     runs to completion in the background; the next `isStale()` check
 *     discards the result before mutating the doc.
 *   - The merge + apply pair is atomic w.r.t. cancellation: once it starts,
 *     it finishes and the doc is populated. `Promise.race` then settles on
 *     `_hydrateInternal`'s `{ hit: true }` (it resolves before the queued
 *     timeout callback runs), so caller and doc agree.
 */
export class TranscriptDocCache {
    private readonly _key: string
    private readonly _doc: Y.Doc

    private _isAttached = false
    private _isDestroyed = false
    private _throttleHandle: ReturnType<typeof setTimeout> | null = null
    // Re-entrant `_performWrite` calls (throttle vs. visibilitychange flush)
    // no-op; pending mutations stay in `_pendingUpdates` for the next tick.
    private _writeInFlight = false
    // Tracks the latest in-flight `_performWrite` so `destroy()` can wait for
    // it (and run a second drain pass afterwards) instead of returning early
    // and orphaning any updates that piled up during the write's IDB await.
    // Always set via `_triggerWrite` -- never write to this directly.
    private _activeWrite: Promise<void> = Promise.resolve()
    private _hydrationCancelled = false

    private _pendingUpdates: Uint8Array[] = []
    private _pendingBytes = 0

    // Mirrors the persisted session row so `_performWrite` can pick append vs
    // compact and assign the next sequence without an extra IDB round-trip.
    private _sessionState: SessionState = {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        nextSequence: 0,
        totalBytes: 0,
    }

    // `null` = not opened yet; a resolved `null` = open failed, caching off.
    private _dbPromise: Promise<IDBDatabase | null> | null = null

    constructor(options: TranscriptDocCacheOptions) {
        this._key = options.key
        this._doc = options.doc
    }

    /**
     * Hydrates the bound doc from IDB, then attaches update listeners. Must
     * complete before transport sync so cached bytes are not written back as
     * new deltas.
     */
    init = async (): Promise<HydrateResult> => {
        if (this._isDestroyed) return { hit: false }

        this._hydrationCancelled = false

        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const timeoutPromise = new Promise<HydrateResult>((resolve) => {
            timeoutId = setTimeout(() => {
                this._hydrationCancelled = true
                debug.log('TranscriptDocCache: hydrate timed out on IDB', {
                    key: this._key,
                    timeoutMs: HYDRATE_IDB_TIMEOUT_MS,
                })
                resolve({ hit: false })
            }, HYDRATE_IDB_TIMEOUT_MS)
        })

        let result: HydrateResult
        try {
            result = await Promise.race([this._hydrateInternal(), timeoutPromise])
        } finally {
            if (timeoutId !== undefined) clearTimeout(timeoutId)
        }

        if (this._isDestroyed) return { hit: false }

        this._attachListeners()

        return result
    }

    flush = async (): Promise<void> => {
        if (this._throttleHandle !== null) {
            clearTimeout(this._throttleHandle)
            this._throttleHandle = null
        }
        await this._triggerWrite()
    }

    /**
     * Single entrypoint for kicking off a write. If a write is already in
     * flight, returns its promise (so callers like `flush()` and `destroy()`
     * can await actual completion) without starting a competing one. The
     * re-entrancy guard inside `_performWrite` itself is what makes the
     * "return existing promise" path safe.
     */
    private _triggerWrite = (compactThreshold?: number): Promise<void> => {
        if (this._writeInFlight) return this._activeWrite
        this._activeWrite = this._performWrite(compactThreshold)

        return this._activeWrite
    }

    /**
     * Tears down listeners and runs a final IDB drain. Returns a promise that
     * resolves once the drain settles, so tests can use it as a deterministic
     * barrier. React cleanup callers can keep ignoring the return value.
     *
     * Uses the opportunistic threshold so an unmount is also a compaction
     * point. React unmount is synchronous -- if the tab survives the IDB
     * transaction, both append and compact commit atomically; if not, IDB
     * aborts and the prior log is retained.
     *
     * Drain ordering: detach listeners first (so `_pendingUpdates` is frozen),
     * then await any in-flight write, then run a second `_performWrite` pass
     * to flush updates that arrived between the in-flight write's snapshot
     * and the destroy call. Without that second pass, those updates would be
     * stranded -- `_performWrite`'s re-entrancy guard would no-op the destroy
     * call's write while the prior one was still awaiting IDB.
     *
     * A fast same-key remount (e.g. metadata churn re-runs the effect) can
     * race this drain against the new instance's hydrate / first write. The
     * race is benign: IDB serialises `readwrite` transactions on the same
     * store, and Y.js chunks are CRDT-substitutable, so a sequence-N collision
     * degrades to one wasted write -- never a corrupt row or lost edits (the
     * server already has them).
     */
    destroy = (): Promise<void> => {
        if (this._isDestroyed) return Promise.resolve()

        if (this._throttleHandle !== null) {
            clearTimeout(this._throttleHandle)
            this._throttleHandle = null
        }

        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._onVisibilityChange)
        }

        if (this._isAttached) {
            this._doc.off('update', this._onDocUpdate)
            this._isAttached = false
        }

        this._isDestroyed = true

        return this._finalDrain()
    }

    private _finalDrain = async (): Promise<void> => {
        // Wait for any in-flight write so we observe pending updates that
        // arrived after its synchronous snapshot. Listeners are already
        // detached at this point, so the pending array is frozen for the
        // duration of this await.
        await this._activeWrite

        const hasPending = this._pendingUpdates.length > 0
        const shouldCompact = this._sessionState.totalBytes > COMPACT_OPPORTUNISTIC_THRESHOLD_BYTES
        if (hasPending || shouldCompact) {
            // Direct call instead of `_triggerWrite`: `_writeInFlight` is false
            // here (the awaited write released it), no other path can fire one
            // after destroy (throttle cleared, all listeners detached), and we
            // need to await the actual write -- not whatever `_activeWrite`
            // happens to hold.
            await this._performWrite(COMPACT_OPPORTUNISTIC_THRESHOLD_BYTES)
        }
    }

    private _onDocUpdate = (update: Uint8Array): void => {
        if (this._isDestroyed) return
        this._pendingUpdates.push(update)
        this._pendingBytes += update.byteLength
        if (this._throttleHandle !== null) return

        this._throttleHandle = setTimeout(() => {
            this._throttleHandle = null
            void this._triggerWrite()
        }, WRITE_THROTTLE_MS)
    }

    private _onVisibilityChange = (): void => {
        if (typeof document === 'undefined') return
        if (document.visibilityState !== 'hidden') return
        if (this._throttleHandle !== null) {
            clearTimeout(this._throttleHandle)
            this._throttleHandle = null
        }
        // Same threshold as `destroy()` -- tab is hidden, freeze is invisible,
        // so we lean into compaction whenever the log has any meaningful size.
        void this._triggerWrite(COMPACT_OPPORTUNISTIC_THRESHOLD_BYTES)
    }

    private _attachListeners = (): void => {
        if (this._isDestroyed || this._isAttached) return

        this._isAttached = true
        this._doc.on('update', this._onDocUpdate)

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._onVisibilityChange)
        }
    }

    private _hydrateInternal = async (): Promise<HydrateResult> => {
        // Re-checked at every await boundary so a torn-down or timed-out
        // hydrate does not belatedly mutate a doc the caller has moved past.
        const isStale = (): boolean => this._isDestroyed || this._hydrationCancelled

        try {
            const db = await this._getDb()
            if (!db || isStale()) return { hit: false }

            const session = await getSession(db, this._key)
            if (isStale()) return { hit: false }
            if (!session) return { hit: false }

            if (session.schemaVersion !== CURRENT_SCHEMA_VERSION) {
                debug.log('TranscriptDocCache: schema mismatch, discarding session', {
                    key: this._key,
                    stored: session.schemaVersion,
                    current: CURRENT_SCHEMA_VERSION,
                })
                await deleteSession(db, this._key)

                return { hit: false }
            }

            const chunks = await getChunks(db, this._key)
            if (isStale()) return { hit: false }
            if (chunks.length === 0) {
                // Orphan session row with no chunks -- wipe so eviction
                // accounting stays consistent with reality.
                await deleteSession(db, this._key)

                return { hit: false }
            }

            try {
                const merged = mergeUpdatesChunked(chunks)
                Y.applyUpdate(this._doc, merged)
            } catch (error) {
                debug.log('TranscriptDocCache: applyUpdate failed, discarding session', { key: this._key, error })
                await deleteSession(db, this._key)

                return { hit: false }
            }

            this._sessionState = {
                schemaVersion: session.schemaVersion,
                nextSequence: session.nextSequence,
                totalBytes: session.totalBytes,
            }

            // Bump lastWrittenAt so a freshly-hydrated session stays at the
            // top of the LRU list even if no new updates land before destroy.
            // The doc is already populated at this point; an LRU-bump failure
            // must NOT degrade the result to `{ hit: false }`, otherwise the
            // caller treats a successful hydration as a cache miss and the
            // loading overlay stays up unnecessarily. Swallow + log instead.
            await putSession(db, { ...session, lastWrittenAt: Date.now() }).catch((error) => {
                debug.log('TranscriptDocCache: failed to bump lastWrittenAt after hydrate', { key: this._key, error })
            })

            return { hit: true }
        } catch (error) {
            debug.log('TranscriptDocCache: hydrate failed', { key: this._key, error })

            return { hit: false }
        }
    }

    /**
     * Drains pending updates and writes them to IDB. Path selection:
     *   - If the resulting log would exceed `compactThreshold`, run the
     *     compact path (mergeUpdates over existing chunks + new chunk, then
     *     replace the whole log with one chunk at sequence 0).
     *   - Otherwise, run the append path (one chunk put at the next sequence).
     *
     * `compactThreshold` defaults to `COMPACT_FORCE_THRESHOLD_BYTES`
     * (mid-session throttle tick); `destroy()` and `_onVisibilityChange` pass
     * `COMPACT_OPPORTUNISTIC_THRESHOLD_BYTES` so those moments compact more
     * eagerly.
     *
     * On failure, the captured batch is restored to `_pendingUpdates` so the
     * next throttle retries -- otherwise a transient IDB failure would lose
     * the in-memory updates.
     */
    private _performWrite = async (compactThreshold: number = COMPACT_FORCE_THRESHOLD_BYTES): Promise<void> => {
        if (this._writeInFlight) return

        const hasPending = this._pendingUpdates.length > 0
        const totalBytes = this._sessionState.totalBytes
        const shouldCompactOnly = !hasPending && totalBytes > compactThreshold

        if (!hasPending && !shouldCompactOnly) return

        this._writeInFlight = true

        // Snapshot pending synchronously -- concurrent mutations during the
        // await go into a fresh array and trigger a fresh throttle.
        const batch = this._pendingUpdates
        const batchBytes = this._pendingBytes
        this._pendingUpdates = []
        this._pendingBytes = 0

        const newChunk = batch.length > 0 ? mergeUpdatesChunked(batch) : null
        const newChunkBytes = newChunk?.byteLength ?? 0
        const projectedTotal = totalBytes + newChunkBytes
        const shouldCompact = projectedTotal > compactThreshold
        // Eviction only has work when the `sessions` row count grows -- i.e.
        // when this write is creating a new row. Captured pre-write so the
        // post-write bump doesn't mask it.
        const isCreatingSession = this._sessionState.nextSequence === 0

        try {
            const db = await this._getDb()
            if (!db) return

            const now = Date.now()

            if (shouldCompact) {
                const existingChunks = await getChunks(db, this._key)
                const allUpdates = newChunk !== null ? [...existingChunks, newChunk] : existingChunks
                if (allUpdates.length === 0) return

                const merged = mergeUpdatesChunked(allUpdates)
                const sessionRecord: SessionRecord = {
                    sessionKey: this._key,
                    schemaVersion: CURRENT_SCHEMA_VERSION,
                    lastWrittenAt: now,
                    nextSequence: 1,
                    totalBytes: merged.byteLength,
                }
                const mergedChunk: ChunkRecord = {
                    sessionKey: this._key,
                    sequence: 0,
                    update: merged,
                }
                await compactSession(db, sessionRecord, mergedChunk)

                this._sessionState = {
                    schemaVersion: CURRENT_SCHEMA_VERSION,
                    nextSequence: 1,
                    totalBytes: merged.byteLength,
                }

                debug.log('TranscriptDocCache: compacted log', {
                    key: this._key,
                    chunkCount: allUpdates.length,
                    bytes: merged.byteLength,
                })
            } else if (newChunk !== null) {
                const sequence = this._sessionState.nextSequence
                const sessionRecord: SessionRecord = {
                    sessionKey: this._key,
                    schemaVersion: CURRENT_SCHEMA_VERSION,
                    lastWrittenAt: now,
                    nextSequence: sequence + 1,
                    totalBytes: projectedTotal,
                }
                const chunk: ChunkRecord = {
                    sessionKey: this._key,
                    sequence,
                    update: newChunk,
                }
                await appendChunk(db, sessionRecord, chunk)

                this._sessionState = {
                    schemaVersion: CURRENT_SCHEMA_VERSION,
                    nextSequence: sequence + 1,
                    totalBytes: projectedTotal,
                }
            }

            if (isCreatingSession) await this._evict(db)
        } catch (error) {
            // Restore the failed batch so the next mutation re-arms the
            // throttle and retries the write. Prepend so any updates that
            // arrived during the await stay chronologically after.
            if (batch.length > 0) {
                this._pendingUpdates = [...batch, ...this._pendingUpdates]
                this._pendingBytes += batchBytes
            }
            debug.log('TranscriptDocCache: write failed', { key: this._key, error })
        } finally {
            this._writeInFlight = false
        }
    }

    private _evict = async (db: IDBDatabase): Promise<void> => {
        try {
            const keys = await findSessionsToEvict(db, MAX_CACHED_SESSIONS, this._key)
            for (const key of keys) {
                await deleteSession(db, key)
            }
        } catch (error) {
            debug.log('TranscriptDocCache: eviction failed', { error })
        }
    }

    private _getDb = (): Promise<IDBDatabase | null> => {
        if (this._dbPromise === null) {
            this._dbPromise = openCacheDb().catch((error) => {
                debug.log('TranscriptDocCache: open failed; caching disabled', { error })

                return null
            })
        }

        return this._dbPromise
    }
}
